// Payment.js - Square Web Payments card form
// Loads Square SDK dynamically, mounts card widget, tokenizes on submit.
// Booking status update (pending_payment → pending) is handled server-side.
//
// Double-card prevention:
//   - initRef guards against React StrictMode double-mounting (fires useEffect twice in dev)
//   - card.destroy() is called in cleanup so the next mount always starts clean
//   - autoComplete="off" suppresses the browser saved-card overlay above the Square iframe
//
// Visual redesign: booking summary, order breakdown with service fee, trust badges,
// premium pay button. Payment logic is untouched.

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { db, auth } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

// Service fee rate shown at checkout — per CLAUDE.md "3-5% added at checkout"
const SERVICE_FEE_RATE = 0.034;

function Payment() {
  const navigate = useNavigate();
  const location = useLocation();

  // bookingId is passed via router state from the Booking page
  const { bookingId } = location.state || {};

  const [bookingDetails, setBookingDetails] = useState(null);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [squareReady, setSquareReady] = useState(false);

  // cardRef holds the Square card widget instance for tokenize() and destroy()
  const cardRef = useRef(null);

  // initRef prevents double-initialization — React StrictMode unmounts+remounts
  // in development, which would call card.attach() twice on the same #card-container
  const initRef = useRef(false);

  useEffect(() => {
    // Redirect if no bookingId was passed from the Booking page
    if (!bookingId) {
      navigate('/book');
      return;
    }

    // Guard: skip if already initialized (StrictMode fires this effect twice in dev)
    if (initRef.current) return;
    initRef.current = true;

    // Fetch booking details for the service summary display and confirmation email
    const fetchBooking = async () => {
      try {
        const bookingDoc = await getDoc(doc(db, 'bookings', bookingId));
        if (bookingDoc.exists()) setBookingDetails(bookingDoc.data());
      } catch (err) {
        console.error('Error fetching booking:', err);
      }
    };
    fetchBooking();

    // Load Square SDK — reuse the script tag if another page already injected it
    const existingScript = document.querySelector(
      'script[src="https://web.squarecdn.com/v1/square.js"]'
    );

    if (existingScript) {
      // Script already in DOM — SDK may already be ready or still loading
      if (window.Square) {
        initializeSquare();
      } else {
        existingScript.addEventListener('load', initializeSquare, { once: true });
      }
    } else {
      // First load — inject the script tag
      const script = document.createElement('script');
      script.src = 'https://web.squarecdn.com/v1/square.js';
      script.onload = initializeSquare;
      document.head.appendChild(script);
    }

    // Cleanup: destroy the card widget so the next mount starts with a clean container.
    // Without this, React StrictMode's second mount would try to attach to a container
    // that already has a Square iframe inside it, producing two visible card inputs.
    return () => {
      if (cardRef.current) {
        try { cardRef.current.destroy(); } catch { /* ignore if already gone */ }
        cardRef.current = null;
      }
      // Remove the script tag so the next full mount reloads it cleanly
      const script = document.querySelector(
        'script[src="https://web.squarecdn.com/v1/square.js"]'
      );
      if (script) document.head.removeChild(script);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Called once the Square SDK script has fully loaded
  const initializeSquare = async () => {
    // Safety check — don't re-attach if a card is already mounted
    if (cardRef.current) return;

    try {
      const payments = window.Square.payments(
        process.env.REACT_APP_SQUARE_APPLICATION_ID,
        process.env.REACT_APP_SQUARE_LOCATION_ID
      );

      // Build the card widget and attach to #card-container
      const card = await payments.card();
      await card.attach('#card-container');

      // Store instance so handleSubmit can call tokenize() later
      cardRef.current = card;
      setSquareReady(true);
    } catch (err) {
      console.error('Square initialization error:', err);
      setError('Could not initialize payment form. Please refresh and try again.');
    }
  };

  const handleSubmit = async () => {
    if (!cardRef.current) return;

    try {
      setLoading(true);
      setError('');

      // Tokenize the card — Square converts card details into a secure single-use token
      const result = await cardRef.current.tokenize();

      if (result.status !== 'OK') {
        setError('Could not process your card. Please check your details and try again.');
        setLoading(false);
        return;
      }

      // Get Firebase ID token to authenticate the payment request.
      // /create-payment now requires auth and verifies booking ownership server-side.
      const idToken = await auth.currentUser.getIdToken();

      // Send the token (sourceId) and bookingId to the backend.
      // Server fetches the booking, computes price server-side, and charges Square.
      const response = await fetch(`${backendUrl}/create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ bookingId, sourceId: result.token }),
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.error || 'Payment processing failed. Please try again.');
        setLoading(false);
        return;
      }

      // Payment succeeded — queue confirmation email then navigate to success page
      setTimeout(() => {
        emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_BOOKING,
          {
            customer_name: auth.currentUser?.displayName || auth.currentUser?.email || 'Valued Customer',
            service_type:  'Grave Visit',
            cemetery_name: bookingDetails?.cemeteryName  || '',
            stone_size:    '',
            to_email:      bookingDetails?.customerEmail  || auth.currentUser?.email,
          },
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        ).catch((err) => console.error('EmailJS error:', err));
      }, 0);

      navigate('/booking-success', {
        state: {
          bookingId,
          lovedOneName:  bookingDetails?.lovedOneName,
          preferredDate: bookingDetails?.preferredDate,
        }
      });

    } catch (err) {
      console.error('Payment submission error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  // Compute display totals from booking fields — server re-verifies on submit
  const addOnTotal = bookingDetails?.addOns
    ? bookingDetails.addOns.reduce((sum, a) => sum + a.price, 0)
    : 0;
  const basePriceDollars = bookingDetails ? (bookingDetails.basePrice || 59) + addOnTotal : 0;
  const feeDollars = basePriceDollars
    ? parseFloat((basePriceDollars * SERVICE_FEE_RATE).toFixed(2))
    : 0;
  const totalDollars = basePriceDollars
    ? parseFloat((basePriceDollars + feeDollars).toFixed(2))
    : 0;

  // Capitalize helper for city / cemetery display
  const capitalize = (str) => {
    if (!str) return '';
    return str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Format preferred date for the booking summary
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
    });
  };

  // Full-page error state for unrecoverable failures (e.g. Square SDK blocked)
  if (error && !squareReady) {
    return (
      <div className="booking-page">
        <a href="/dashboard" className="auth-logo">Evermark Memorial</a>
        <div className="booking-card-form">
          <div className="auth-error">{error}</div>
          <button className="btn-primary btn-full" onClick={() => navigate('/book')}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <a href="/dashboard" className="auth-logo">Evermark Memorial</a>

      {/* payment-card-premium adds green top border + elevated shadow */}
      <div className="booking-card-form payment-card-premium">
        <h2>Complete Your Booking</h2>
        <p className="auth-subtitle">
          Your visit will be confirmed after payment
        </p>

        {/*
          autoComplete="off" on the wrapper suppresses the browser saved-card overlay
          that would otherwise appear above the Square iframe.
        */}
        <div
          className="payment-form"
          role="form"
          aria-label="Payment form"
          autoComplete="off"
        >

          {/* ========== PROGRESS INDICATOR ==========
              Step 2 is active — Details completed, Payment in progress */}
          <div className="progress-bar" style={{ marginBottom: '28px' }}>
            <div className="progress-step progress-completed">
              <div className="progress-circle">✓</div>
              <span className="progress-label">Details</span>
            </div>
            <div className="progress-line progress-line-completed"></div>
            <div className="progress-step progress-active">
              <div className="progress-circle">2</div>
              <span className="progress-label">Payment</span>
            </div>
            <div className="progress-line"></div>
            <div className="progress-step progress-upcoming">
              <div className="progress-circle">3</div>
              <span className="progress-label">Confirmed</span>
            </div>
          </div>

          {/* ========== BOOKING SUMMARY ==========
              Shown once bookingDetails loads from Firestore */}
          {bookingDetails && (
            <div className="payment-booking-summary">
              <div className="payment-summary-row">
                🌿 <strong>Grave Visit</strong> — Headstone Cleaning
              </div>
              {/* List any selected add-ons below the base service */}
              {bookingDetails.addOns?.length > 0 && (
                <div className="payment-summary-row payment-summary-addons">
                  {bookingDetails.addOns.map(a => (
                    <span key={a.id} className="payment-addon-tag">+ {a.label}</span>
                  ))}
                </div>
              )}
              <div className="payment-summary-row">
                📍 {capitalize(bookingDetails.cemeteryName)}, {capitalize(bookingDetails.cemeteryCity)} {bookingDetails.cemeteryState?.toUpperCase()}
              </div>
              <div className="payment-summary-row">
                👤 For: {bookingDetails.lovedOneName}
              </div>
              <div className="payment-summary-row">
                📅 Preferred date: {formatDate(bookingDetails.preferredDate)}
              </div>
            </div>
          )}

          <hr className="payment-section-divider" />

          {/* ========== ORDER SUMMARY ==========
              Base + each add-on + service fee + total. Server re-verifies actual charge. */}
          {bookingDetails && (
            <div className="payment-order-summary">
              {/* Base visit line */}
              <div className="payment-order-row">
                <span>Base Visit (Headstone Cleaning)</span>
                <span>${(bookingDetails.basePrice || 59).toFixed(2)}</span>
              </div>
              {/* One row per add-on */}
              {bookingDetails.addOns?.map(a => (
                <div key={a.id} className="payment-order-row">
                  <span>{a.label}</span>
                  <span>+${a.price.toFixed(2)}</span>
                </div>
              ))}
              <div className="payment-order-row">
                <span>Service fee</span>
                <span>${feeDollars.toFixed(2)}</span>
              </div>
              <hr className="payment-order-inner-divider" />
              <div className="payment-order-row payment-order-total-row">
                <span>Total due today</span>
                <span>${totalDollars.toFixed(2)}</span>
              </div>
            </div>
          )}

          <hr className="payment-section-divider" />

          {/* ========== PAYMENT DETAILS HEADING ========== */}
          <h3 style={{ marginBottom: '16px' }}>Payment Details</h3>

          {/* Accepted card brands — small outlined pill badges */}
          <div className="payment-card-brands">
            <span className="payment-card-brands-label">Accepted:</span>
            {['VISA', 'MC', 'AMEX', 'DISC'].map(brand => (
              <span key={brand} className="payment-card-brand">{brand}</span>
            ))}
          </div>

          {/* Label above the Square iframe */}
          <p className="payment-card-field-label">Card Information</p>

          {/* Spinner shown while Square SDK initializes */}
          {!squareReady && (
            <div className="dashboard-loading" style={{ minHeight: 'auto', padding: '40px 0' }}>
              <div className="loading-spinner"></div>
              <p>Setting up secure payment...</p>
            </div>
          )}

          {/*
            Square card widget mounts into this div.
            Must always be present in the DOM (Square needs the element to exist
            when attach() is called). Hidden via CSS until squareReady is true.
            The explicit styling here ensures it is clearly visible and is the
            only card input shown — no invisible/zero-height container that the
            browser could mistake for an autofill target.
          */}
          <div
            id="card-container"
            style={{
              display:      squareReady ? 'block' : 'none',
              minHeight:    '100px',
              padding:      '16px',
              border:       '1.5px solid #e0e0e0',
              borderRadius: '12px',
              background:   '#fafafa',
              marginBottom: '16px',
            }}
          ></div>

          {/* Inline error for submission failures */}
          {error && <div className="auth-error">{error}</div>}

          {/* ========== PAY BUTTON ==========
              Premium green gradient, 56px height, SVG lock icon, spinner on loading */}
          <button
            className="btn-pay"
            onClick={handleSubmit}
            disabled={loading || !squareReady}
            style={{ display: squareReady ? 'flex' : 'none' }}
          >
            {loading ? (
              <>
                <span className="btn-pay-spinner" />
                Processing...
              </>
            ) : (
              <>
                {/* Inline SVG lock icon */}
                <svg
                  width="16" height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0 }}
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Pay ${totalDollars.toFixed(2)} Securely
              </>
            )}
          </button>

          {/* ========== TRUST BADGES ==========
              Replaces the old emoji lock line */}
          <div className="payment-trust-bar">
            <span className="payment-trust-badge">🔒 SSL Encrypted</span>
            <span className="payment-trust-badge">Square Secure</span>
            <span className="payment-trust-badge">256-bit Protection</span>
          </div>
          <p className="payment-trust-note">
            Your card information is never stored on our servers.
          </p>

        </div>

        <p className="auth-switch">
          <a href="/book">← Back to Booking</a>
        </p>
      </div>
    </div>
  );
}

export default Payment;
